import { Alert, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { BillingSubscriptionDTO, UpdateMeResponse } from '@inversor-hub/types';
import { useCreateBillingPortalSession, useGetBillingSubscription } from '@/lib/api/billing.ts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import {
  formatBillingDate,
  planLabelMap,
  statusLabelMap,
} from '@/features/billing/billing-labels.ts';

interface BillingSectionProps {
  currentUser: UpdateMeResponse;
}

const getCheckoutAlert = (
  checkoutStatus: string | null,
  currentPlan?: BillingSubscriptionDTO['currentPlan']
) => {
  if (checkoutStatus === 'cancel') {
    return {
      severity: 'info' as const,
      title: 'No se completó la suscripción',
      message: 'Puedes volver a intentarlo cuando quieras desde esta sección.',
    };
  }

  if (checkoutStatus === 'success' && currentPlan === 'STARTER') {
    return {
      severity: 'info' as const,
      title: 'Estamos confirmando tu suscripción',
      message:
        'Stripe ha terminado el checkout, pero el estado final depende de la sincronización con el backend.',
    };
  }

  if (checkoutStatus === 'success') {
    return {
      severity: 'success' as const,
      title: 'Suscripción actualizada',
      message: 'Tu estado de suscripción ya se ha sincronizado correctamente.',
    };
  }

  return null;
};

const statusColorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  ACTIVE: 'success',
  TRIALING: 'info',
  PAST_DUE: 'warning',
  UNPAID: 'error',
  CANCELED: 'default',
  INCOMPLETE: 'warning',
  NONE: 'default',
};

const BillingSection = ({ currentUser }: BillingSectionProps) => {
  const { data: billingSubscription, isLoading: isLoadingSubscription } = useGetBillingSubscription(
    {
      enabled: currentUser.role === 'investor',
    }
  );
  const createPortalSession = useCreateBillingPortalSession();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { show: showSnackbar } = useSnackbar();

  const checkoutAlert = getCheckoutAlert(
    searchParams.get('checkout'),
    billingSubscription?.currentPlan
  );

  const handleManageSubscription = async () => {
    try {
      const url = await createPortalSession.mutateAsync();
      window.location.assign(url);
    } catch {
      showSnackbar({
        title: 'No pudimos abrir el portal de Stripe',
        message: 'Inténtalo de nuevo en unos minutos.',
        variant: 'error',
      });
    }
  };

  const handleOpenPlans = () => {
    navigate('/planes');
  };

  if (isLoadingSubscription || !billingSubscription) {
    return <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />;
  }

  const maxOwnedInvestmentsLabel =
    billingSubscription.maxOwnedInvestments === null
      ? 'Sin límite'
      : `Hasta ${billingSubscription.maxOwnedInvestments} ${
          billingSubscription.maxOwnedInvestments === 1 ? 'inversión' : 'inversiones'
        }`;

  const currentPlanLabel = planLabelMap[billingSubscription.currentPlan];

  return (
    <Stack spacing={2}>
      {checkoutAlert ? (
        <Alert severity={checkoutAlert.severity}>
          <strong>{checkoutAlert.title}</strong> {checkoutAlert.message}
        </Alert>
      ) : null}

      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
              Plan {currentPlanLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {maxOwnedInvestmentsLabel}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip
              label={statusLabelMap[billingSubscription.subscriptionStatus]}
              size="small"
              color={statusColorMap[billingSubscription.subscriptionStatus] ?? 'default'}
              variant="outlined"
            />
            {billingSubscription.entitlementSource === 'GRANT' ? (
              <Chip label="Acceso concedido" size="small" color="success" variant="outlined" />
            ) : null}
            {billingSubscription.billingCycle ? (
              <Chip
                label={billingSubscription.billingCycle === 'YEARLY' ? 'Anual' : 'Mensual'}
                size="small"
                variant="outlined"
              />
            ) : null}
          </Stack>
        </Stack>

        {billingSubscription.cancelAtPeriodEnd && billingSubscription.currentPeriodEnd ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Tu suscripción seguirá activa hasta el{' '}
            {formatBillingDate(billingSubscription.currentPeriodEnd)}.
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {billingSubscription.currentPlan === 'STARTER'
              ? 'Tu cuenta sigue en el plan gratuito con una inversión incluida.'
              : billingSubscription.entitlementSource === 'GRANT'
                ? 'Tu acceso a este plan está concedido por InversorHub.'
                : billingSubscription.currentPeriodEnd
                  ? `Tu periodo actual está activo hasta el ${formatBillingDate(billingSubscription.currentPeriodEnd)}.`
                  : 'Tu suscripción está activa y sincronizada con Stripe.'}
          </Typography>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="contained" endIcon={<EastOutlinedIcon />} onClick={handleOpenPlans}>
            Ver planes y precios
          </Button>

          {billingSubscription.canManageBilling ? (
            <Button
              variant="outlined"
              endIcon={<OpenInNewOutlinedIcon />}
              onClick={handleManageSubscription}
              disabled={createPortalSession.isPending}
            >
              Gestionar en Stripe
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default BillingSection;
